"use client";

import type { RefObject } from "react";
import type { CategoryStyle } from "@/lib/validation/design";
import { faShapes } from "@fortawesome/pro-solid-svg-icons";
import { useFormatter, useTranslations } from "next-intl";
import { useLayoutEffect, useRef, useState } from "react";
import { ScrollRail } from "@/components/storefront/ScrollRail";
import { Icon } from "@/components/ui/Icon";
import { Link } from "@/i18n/navigation";

/** One storefront-visible category, already localized and counted. */
export interface CategoryChip {
	id: string;
	slug: string;
	label: string;
	image?: string | null;
	/** Published products in this category's whole subtree. */
	count?: number;
	/** Which category this sits under, `null` for a root. */
	parentId?: string | null;
}

/**
 * The storefront's ONE category surface.
 *
 * It replaced three: a grid of 4:3 photo tiles with the name and an item count
 * burnt over the picture, a wrapping row of filter chips under the catalogue
 * heading, and whichever of the two the merchant had also copied into the
 * header nav. A shopper could read the same category name three times on one
 * screen (Trello "categories redo", "Categories", "Remove these").
 *
 * Two decisions carry the rest:
 *
 * **The label is beside the tile, never over it.** A name burnt into a
 * merchant's photograph is legible or not depending on the photograph, and the
 * gradient scrim that made it legible is what made the old grid heavy. Outside
 * the tile the name is on the page's own text colour, so it survives a dark
 * theme, a pale photo and a missing photo alike.
 *
 * **The rail shows ONE level and drills down.** The chip row existed because
 * a shopper who picked a root had nowhere left to go: the grid vanished, and
 * only a flat list of every category could still reach a child. Picking a root
 * here swaps the rail to that root's children, so the level below is where the
 * shopper is already looking — and `?category=` selects the root's whole
 * subtree (`categorySubtree`), so the root is a real shelf rather than a menu.
 *
 * ⚠️ That makes this the ONLY way to reach a category, so every category has
 * to appear on some level of it. See `effectiveParent`: a child whose parent is
 * missing would otherwise sit on a level nothing can open.
 *
 * `variant` only changes how one category is drawn; the levels, the "all" tile
 * and the drill-down are the same in every style.
 */
export function CategoryRail({
	categories,
	activeSlug,
	title,
	variant = "tiles",
	sticky = false,
	headerSticky = false,
}: {
	/** Every category, depth-first; `orderedCategoryTree` guarantees none is missing. */
	categories: CategoryChip[];
	activeSlug: string | null;
	title: string;
	variant?: CategoryStyle;
	/** Pin the row (not the title) to the top of the viewport while scrolling. */
	sticky?: boolean;
	/** The storefront header is itself pinned, so the row pins BELOW it. */
	headerSticky?: boolean;
}) {
	const t = useTranslations("shop");
	const bandRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const top = useStickyHeaderHeight(bandRef, sticky && headerSticky);
	const compact = useStuck(sentinelRef, bandRef, sticky);
	useSteadyFootprint(bandRef, compact);

	/*
	 * The parent the rail files a category under, which is not always
	 * `parentId`. On a storefront the list is already filtered to active,
	 * not-deleted rows, so a child of a DEACTIVATED parent names a row that is
	 * not here. Filing it under that missing id would put it on a level no tile
	 * opens, and with the toolbar's category picker gone there is no second way
	 * in: its products would be unreachable. It is shown as a root instead,
	 * which is what `orderedCategoryTree` does with the same row.
	 */
	const present = new Set(categories.map((c) => c.id));
	const effectiveParent = (c: CategoryChip): string | null =>
		c.parentId && present.has(c.parentId) ? c.parentId : null;

	const active = activeSlug ? (categories.find((c) => c.slug === activeSlug) ?? null) : null;
	// The level whose siblings the rail shows. A root with children opens them;
	// a root without children, and a child, both stay among their own siblings.
	const activeParentId = active ? effectiveParent(active) : null;
	const parent = active
		? activeParentId
			? (categories.find((c) => c.id === activeParentId) ?? null)
			: categories.some((c) => effectiveParent(c) === active.id)
				? active
				: null
		: null;
	const level = categories.filter((c) => effectiveParent(c) === (parent?.id ?? null));

	if (level.length === 0) return null;

	const text = variant === "text";
	const railFor = (isCompact: boolean) => (
		<ScrollRail
			label={{ prev: t("railPrev", { name: title }), next: t("railNext", { name: title }) }}
			snap
			// The chosen category has to be visible on arrival, not just marked:
			// a rail long enough to scroll opens at its start, and a deep link
			// into the twelfth category otherwise looks like nothing is chosen.
			// The key is the slug, so a navigation to another category moves the
			// rail and a re-render of the same one leaves it alone.
			reveal={activeSlug ?? "all"}
			contentClassName={text ? "flex gap-2 py-1" : `flex ${isCompact ? "gap-3" : "gap-4"} pb-1`}
			// The tiles, not the tile-plus-label block: an arrow centred on the
			// whole item sits low enough to read as belonging to the caption. A
			// pill has no caption, so its arrows keep the rail's own centre.
			arrowClassName={
				text
					? undefined
					: `${isCompact ? "top-[calc(50%-0.6rem)]" : "top-[calc(50%-1.4rem)]"} -translate-y-1/2`
			}
		>
			{/* "Everything in this level" leads, so the current scope is always
			    the first thing in reading order. */}
			<RailTile
				variant={variant}
				compact={isCompact}
				href={parent ? `/?category=${parent.slug}` : "/"}
				label={parent ? t("allOf", { name: parent.label }) : t("all")}
				active={parent ? activeSlug === parent.slug : !activeSlug}
				count={parent?.count}
				// Not a monogram. "الكل" would render a bare ا and read as a
				// category whose name starts with alif, which is most of them.
				icon
			/>
			{level.map((c) => (
				<RailTile
					key={c.id}
					variant={variant}
					compact={isCompact}
					href={`/?category=${c.slug}`}
					label={c.label}
					image={c.image}
					active={activeSlug === c.slug}
					count={c.count}
				/>
			))}
		</ScrollRail>
	);

	return (
		<>
			<section className="mx-auto max-w-6xl px-6 pt-10">
				<div
					className={`${sticky ? "mb-1" : "mb-4"} flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1`}
				>
					{/*
				  Always the section's own title, never the open category's name: the
				  catalogue heading below already carries that, and printing it twice
				  on one screen is the thing this card is about. Which level is open is
				  said once, by the highlighted "all of X" tile.
				*/}
					<h2 className="text-xl font-bold">{title}</h2>
					{/*
				  The way back up, and the only "clear" affordance the rail needs.
				  The removed chip row spent a whole chip on it at every level.
				*/}
					{parent && (
						<Link
							href="/"
							className="text-sm font-medium text-muted-foreground underline-offset-4 transition hover:text-primary hover:underline"
						>
							{t("allCategories")}
						</Link>
					)}
				</div>

				{!sticky && railFor(false)}
			</section>
			{/*
			  The pinned band is the section's SIBLING, not its child: `sticky` holds
			  only inside its parent, and the section ends right under the row. As a
			  direct child of `<main>` it stays pinned for the rest of the page. The
			  title stays behind in the flow, so the band is only the row itself.
			*/}
			{/*
			  Zero height, directly above the band: while the band sits on it the
			  band is in the flow, and once the band is below it the band is stuck.
			*/}
			{sticky && <div ref={sentinelRef} aria-hidden />}
			{sticky && (
				<div
					ref={bandRef}
					data-category-band=""
					className="sticky z-20 border-b border-border bg-bg/85 backdrop-blur supports-[backdrop-filter]:bg-bg/75"
					style={{ top: top ?? 0 }}
				>
					<div className={`mx-auto max-w-6xl px-6 ${compact ? "py-1" : "py-2"}`}>
						{railFor(compact)}
					</div>
				</div>
			)}
		</>
	);
}

/**
 * The height of the storefront's own header, where the pinned band has to sit.
 *
 * Measured, not a constant: the header grows a mobile search row and a nav row
 * depending on the store's settings and the viewport. It is looked up inside
 * the same `[data-storefront]` root so the design editor's preview measures the
 * preview's header and not the panel's. `null` when the header is not pinned,
 * so the band pins to the very top.
 *
 * A layout effect, so the observer's first delivery lands before the band's
 * first paint rather than after a frame pinned at `top: 0` under the header.
 * `borderBoxSize` over `offsetHeight` because the entry already carries it.
 */
function useStickyHeaderHeight(
	ref: RefObject<HTMLElement | null>,
	enabled: boolean,
): number | null {
	const [height, setHeight] = useState<number | null>(null);
	useLayoutEffect(() => {
		const header = enabled
			? ref.current?.closest("[data-storefront]")?.querySelector("header")
			: null;
		if (!header) return;
		const observer = new ResizeObserver(([entry]) =>
			setHeight(entry?.borderBoxSize?.[0]?.blockSize ?? header.offsetHeight),
		);
		observer.observe(header);
		return () => observer.disconnect();
	}, [ref, enabled]);
	return enabled ? height : null;
}

/**
 * Whether the pinned band is stuck rather than resting in the flow.
 *
 * Read from geometry, not an IntersectionObserver: the design editor's preview
 * scrolls its own container, so a viewport root margin would be wrong there.
 * The sentinel's position depends only on what is above the band, but the
 * scroll offset does not: a band that shrinks when stuck shortens the page, and
 * the clamped scroll unsticks it again. `useSteadyFootprint` is what keeps the
 * answer from flipping back.
 */
function useStuck(
	sentinel: RefObject<HTMLElement | null>,
	band: RefObject<HTMLElement | null>,
	enabled: boolean,
): boolean {
	const [stuck, setStuck] = useState(false);
	useLayoutEffect(() => {
		if (!enabled) return;
		let frame = 0;
		const measure = () => {
			frame = 0;
			const s = sentinel.current?.getBoundingClientRect();
			const b = band.current?.getBoundingClientRect();
			if (s && b) setStuck(b.top - s.bottom > 1);
		};
		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(measure);
		};
		schedule();
		document.addEventListener("scroll", schedule, { capture: true, passive: true });
		window.addEventListener("resize", schedule, { passive: true });
		return () => {
			cancelAnimationFrame(frame);
			document.removeEventListener("scroll", schedule, { capture: true });
			window.removeEventListener("resize", schedule);
		};
	}, [sentinel, band, enabled]);
	return enabled && stuck;
}

/**
 * Keep the band's footprint in the flow at its resting height while it is
 * drawn compact.
 *
 * The compact band is far shorter than the resting one, and without this the
 * page shortens by the difference the moment the band sticks. Near the pin
 * point that is a loop: on a page with little below the band the shorter page
 * can no longer scroll past the pin point, the browser clamps the scroll back,
 * the band unsticks and grows, the page can scroll again, and it sticks again
 * every frame. Scroll anchoring makes the same loop from a layout shift alone.
 * A bottom margin worth the difference keeps everything under the band where
 * it was, so pinning changes how the band looks and nothing else.
 *
 * Written to the element rather than through state: the observer follows the
 * tiles' width transition frame by frame, and a render per frame would be the
 * only cost of doing it in React.
 */
function useSteadyFootprint(band: RefObject<HTMLElement | null>, compact: boolean): void {
	const restRef = useRef(0);
	useLayoutEffect(() => {
		const el = band.current;
		if (!el) return;
		const apply = (height: number) => {
			if (!compact) restRef.current = height;
			el.style.marginBottom =
				compact && restRef.current > height ? `${restRef.current - height}px` : "";
		};
		apply(el.offsetHeight);
		const observer = new ResizeObserver(([entry]) =>
			apply(entry?.borderBoxSize?.[0]?.blockSize ?? el.offsetHeight),
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [band, compact]);
}

/**
 * One tile plus its caption.
 *
 * A category with no picture is the common case, not the exception — a store
 * has categories from its first day and pictures for them much later — so the
 * fallback is a designed state rather than a placeholder photograph. It is the
 * store's own primary colour at low strength, which keeps a rail of mixed
 * tiles reading as one set.
 */
function RailTile({
	variant,
	compact = false,
	href,
	label,
	image,
	active,
	count,
	icon = false,
}: {
	variant: CategoryStyle;
	/** The band is pinned: a smaller tile, one caption line, no count. */
	compact?: boolean;
	href: string;
	label: string;
	image?: string | null;
	active: boolean;
	count?: number;
	/** Draw the category glyph instead of the name's first letter. */
	icon?: boolean;
}) {
	const t = useTranslations("shop");
	const format = useFormatter();
	if (variant === "text") {
		return (
			<Link
				href={href}
				aria-current={active ? "page" : undefined}
				data-rail-reveal={active ? "" : undefined}
				// Filled in the store's primary when chosen: a pill has no picture to
				// edge, so the fill is what says which one is open.
				className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border text-sm font-medium transition-all ${
					compact ? "px-3 py-1" : "px-4 py-2"
				} ${
					active
						? "border-primary bg-primary text-primary-foreground"
						: "border-border text-text hover:border-primary/40 hover:text-primary"
				}`}
			>
				{label}
				{!compact && typeof count === "number" && count > 0 && (
					<span
						className={`text-xs tabular-nums ${active ? "opacity-80" : "text-muted-foreground"}`}
					>
						{format.number(count)}
					</span>
				)}
			</Link>
		);
	}
	const circle = variant === "circles";
	return (
		<Link
			href={href}
			aria-current={active ? "page" : undefined}
			// What `ScrollRail` scrolls to. One tile carries it at a time, and
			// `aria-current` is not the hook: the rail is generic and an item may be
			// worth revealing without being the page.
			data-rail-reveal={active ? "" : undefined}
			// A circle is narrower than a tile on wide screens: at the tile's width
			// it grows into a disc larger than the product pictures under it.
			className={`group flex shrink-0 flex-col items-center text-center transition-[width] ${
				compact
					? "w-12 gap-1 sm:w-14"
					: `w-[4.25rem] gap-1.5 sm:gap-2 ${circle ? "sm:w-24" : "sm:w-28"}`
			}`}
		>
			<span
				// Every state stays INSIDE the tile's box. The rail is a horizontal
				// scroller, and `overflow-x: auto` clips the other axis too, so a hover
				// lift or an outer `ring` is cut off at the rail's edges. The chosen
				// tile gets an inset edge in the store's primary, drawn by `::after` so
				// it paints over the picture. A resting tile has no edge at all, and hover
				// only brightens the picture and tints the caption.
				className={`relative grid aspect-square w-full place-items-center overflow-hidden bg-surface-2 after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:transition ${
					circle ? "rounded-full" : "rounded-card"
				} ${active ? "after:shadow-[inset_0_0_0_2px_var(--primary)]" : ""}`}
			>
				{image ? (
					// oxlint-disable-next-line nextjs/no-img-element -- merchant-uploaded at arbitrary dimensions
					<img
						src={image}
						alt=""
						loading="lazy"
						className="h-full w-full object-cover transition duration-300 group-hover:scale-105 group-hover:brightness-105"
					/>
				) : (
					<span
						className={`grid h-full w-full place-items-center font-bold ${compact ? "text-sm" : "text-lg sm:text-xl"}`}
						style={{
							background: "color-mix(in oklch, var(--primary) 10%, transparent)",
							color: "color-mix(in oklch, var(--primary) 70%, var(--text))",
						}}
						aria-hidden
					>
						{/* A leading glyph beats a generic icon for telling two
								    categories apart; the icon covers the "everything" tile
								    and a name starting with punctuation or an emoji. */}
						{(icon ? null : firstGlyph(label)) ?? <Icon icon={faShapes} size={18} />}
					</span>
				)}
			</span>

			<span className="flex flex-col gap-0.5">
				{/*
				  Two lines' worth of room whether the name needs it or not, so the
				  counts under a rail of mixed-length names sit on one line instead of
				  stepping down under every long one.
				*/}
				<span
					className={`text-xs leading-tight ${
						compact ? "line-clamp-1 text-[11px]" : "line-clamp-2 min-h-8 sm:min-h-9 sm:text-sm"
					} ${
						active ? "font-bold text-primary" : "font-medium text-text group-hover:text-primary"
					}`}
				>
					{label}
				</span>
				{/*
				  The count is HERE and nowhere else on the page. It used to be a pill
				  burnt over the photo AND implied by the results line under the
				  catalogue heading. On the rail it helps a shopper choose; once a
				  category is chosen the heading carries it, and the rail has already
				  moved to the level below.
				*/}
				{!compact && typeof count === "number" && count > 0 && (
					<span className="text-[10px] text-muted-foreground sm:text-[11px]">
						{t("categoryCount", { n: count })}
					</span>
				)}
			</span>
		</Link>
	);
}

/**
 * The first letter of a name, or `null` when it has none.
 *
 * `Intl.Segmenter` rather than `label[0]`: an Arabic name is fine either way,
 * but a name starting with an emoji or a combining mark would be cut mid
 * grapheme and render as a replacement box.
 */
function firstGlyph(label: string): string | null {
	const trimmed = label.trim();
	if (!trimmed) return null;
	const [first] = new Intl.Segmenter().segment(trimmed);
	return first?.segment ?? null;
}
